import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Download, Eye, Hand, ImageUp, MousePointerClick, Plus, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAnalyticsApi, MetricResult } from '../../../common/hooks/use-analytics-api.hook';
import {
  AnalyticsPeriod,
  BarList,
  DistributionBar,
  Granularity,
  HourStrip,
  MiniStat,
  TermLabel,
  TrendChart,
  defaultGranularity,
  utcDay,
} from '../../../components/design-system/analytics';
import { KpiCard, PillTabs, SectionCard, formatNumber } from '../../../components/design-system/admin';
import { NoTrafficState } from '../../../modules/vendor/analytics/viz/components/NoTrafficState';
import { ReadingCard } from '../../../modules/vendor/analytics/viz/components/ReadingCard';
import { VizFunnel } from '../../../modules/vendor/analytics/viz/components/VizFunnel';
import { deltaOf, noTraffic, pairOf, sparkOf, trendOf, valueOf } from '../../../modules/vendor/analytics/viz/metrics';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';
import { vizReading } from '../../../modules/vendor/analytics/viz/viz-reading';
import { VIZ_LINKS } from '../../../modules/vendor/analytics/viz/links';
import { DashboardExportMenu } from './components/DashboardExportMenu';

type TrendMetric = 'visitors' | 'tries' | 'downloads' | 'uploads' | 'leads';

const TREND_METRICS: Record<TrendMetric, string> = {
  visitors: 'unique_visitors',
  tries: 'product_applications',
  downloads: 'result_downloads',
  uploads: 'space_uploads_completed',
  leads: 'new_leads',
};

const SURFACES = ['floor', 'walls', 'ceiling', 'interactive_floor'] as const;

/** Visits per hour of the viewer's day, summed over the period. */
function hoursOfDay(series: Array<{ date: string; value: number }>): number[] {
  const hours = new Array(24).fill(0);
  for (const point of series) {
    // Hourly buckets only: a fallback daily series would pile every visit onto midnight.
    if (typeof point.date !== 'string' || !point.date.includes('T')) continue;
    const d = new Date(point.date);
    if (!Number.isNaN(d.getTime())) hours[d.getHours()] += Number(point.value) || 0;
  }
  return hours;
}

/**
 * The analytics overview: the deeper read of the dashboard's story — the
 * funnel with its shares, the trend at any grain, who visits (devices,
 * places, hours), what they do, and what they searched for and did not find.
 */
export const ModernAnalyticsDashboard: React.FC<{ period: AnalyticsPeriod }> = ({ period }) => {
  const { t, i18n } = useTranslation();
  const analytics = useAnalyticsApi();
  const locale = (i18n.language || 'en').slice(0, 2);
  const [granularity, setGranularity] = useState<Granularity>(defaultGranularity(period.days));
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('visitors');
  // A new period resets the grain to one that suits its length.
  const [grainFor, setGrainFor] = useState(period.startDate + period.endDate);
  if (grainFor !== period.startDate + period.endDate) {
    setGrainFor(period.startDate + period.endDate);
    setGranularity(defaultGranularity(period.days));
  }

  const { data, loading, error, reload } = useLoader(async () => {
    const { startDate, endDate } = period;
    const dateRange = { startDate, endDate };
    const metric = (name: string, extra: Record<string, unknown> = {}) =>
      analytics.queryMetric({ metric: name, startDate, endDate, compareToPreviousPeriod: true, ...extra });
    const series = { groupBy: 'time' as const, granularity };

    const [visitors, opens, uploads, tries, downloads, leads, shares, engagementRate, trialVisitors, resultVisitors, searches, zeroSearches, devices, surfaces, cities, zeroTerms, hourly, dashboard] =
      await Promise.all([
        metric('unique_visitors', series),
        metric('visualizer_page_views'),
        metric('space_uploads_completed', series),
        metric('product_applications', series),
        metric('result_downloads', series),
        metric('new_leads', series),
        metric('shares'),
        metric('engagement_rate'),
        metric('product_trial_visitors'),
        metric('result_visitors'),
        metric('search_queries'),
        metric('zero_result_searches'),
        analytics.queryMetric({ metric: 'unique_visitors', startDate, endDate, groupBy: 'device' }),
        analytics.queryMetric({ metric: 'product_applications', startDate, endDate, groupBy: 'surface' }),
        analytics.queryMetric({ metric: 'unique_visitors', startDate, endDate, groupBy: 'city', limit: 8 }),
        analytics.getZeroResultSearchTerms(startDate, endDate),
        analytics.getMetricTrend('visits', { dateRange }, 'hour').catch(() => []),
        analytics
          .getDashboardData({ dateRange, comparisonPeriod: { startDate: period.previousStartDate, endDate: period.previousEndDate } })
          .catch(() => null),
      ]);
    return { visitors, opens, uploads, tries, downloads, leads, shares, engagementRate, trialVisitors, resultVisitors, searches, zeroSearches, devices, surfaces, cities, zeroTerms, hourly, dashboard };
  }, [period.startDate, period.endDate, granularity]);

  const series: Record<TrendMetric, MetricResult | null | undefined> = {
    visitors: data?.visitors,
    tries: data?.tries,
    downloads: data?.downloads,
    uploads: data?.uploads,
    leads: data?.leads,
  };

  const reading = useMemo(() => {
    if (!data) return [];
    return vizReading({
      visitors: pairOf(data.visitors),
      tries: pairOf(data.tries),
      downloads: pairOf(data.downloads),
      leads: pairOf(data.leads),
      trialVisitors: valueOf(data.trialVisitors),
      resultVisitors: valueOf(data.resultVisitors),
      uploads: pairOf(data.uploads),
      zeroResultSearches: { count: valueOf(data.zeroSearches) ?? 0, topTerm: data.zeroTerms[0]?.term },
    });
  }, [data]);

  // A link to #searches (from a reading elsewhere) lands on the searches card once it is drawn.
  const location = useLocation();
  useEffect(() => {
    if (!loading && location.hash === '#searches') document.getElementById('searches')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, location.hash]);

  const vsPrevious = t('VIZ_ANALYTICS.COMMON.VS_PREVIOUS', { count: period.days });
  const hours = useMemo(() => hoursOfDay((data?.hourly as any[]) ?? []), [data?.hourly]);
  const hasHours = hours.some((h) => h > 0);

  const kpis = [
    { key: 'visitors', label: t('VIZ_ANALYTICS.KPI.VISITORS'), m: data?.visitors, icon: <Users />, spark: true },
    { key: 'opens', label: t('VIZ_ANALYTICS.KPI.OPENS'), m: data?.opens, icon: <Eye /> },
    { key: 'uploads', label: t('VIZ_ANALYTICS.KPI.UPLOADS'), m: data?.uploads, icon: <ImageUp />, spark: true },
    { key: 'tries', label: t('VIZ_ANALYTICS.KPI.TRIES'), m: data?.tries, icon: <MousePointerClick />, spark: true },
    { key: 'downloads', label: t('VIZ_ANALYTICS.KPI.DOWNLOADS'), m: data?.downloads, icon: <Download />, spark: true },
    { key: 'engagement', label: t('VIZ_ANALYTICS.KPI.ENGAGEMENT_RATE'), m: data?.engagementRate, icon: <Hand />, percent: true },
  ];

  const empty = !loading && !!data && data.visitors !== null && noTraffic(data.visitors);
  const failed = !loading && (error || (data && data.visitors === null));

  if (failed) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">{t('VIZ_ANALYTICS.COMMON.LOAD_FAILED')}</p>
          <Button variant="outline" size="sm" onClick={reload}>
            {t('VIZ_ANALYTICS.COMMON.RETRY')}
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (empty) return <NoTrafficState />;

  const devices = data?.devices?.breakdown ?? [];
  const surfaces = data?.surfaces?.breakdown ?? [];
  const geo = data?.dashboard?.geographicMetrics;
  const eb = data?.dashboard?.engagementBreakdown;
  const behaviour = eb
    ? [
        { key: 'productChange', value: eb.productChange },
        { key: 'spaceSelection', value: eb.spaceSelection },
        { key: 'uploads', value: eb.uploads },
        { key: 'download', value: eb.download },
        { key: 'rotation', value: eb.rotation },
        { key: 'compare', value: eb.compare },
        { key: 'search', value: eb.search },
      ]
        .filter((r) => (r.value || 0) > 0)
        .sort((a, b) => b.value - a.value)
    : [];

  return (
    <div className="flex flex-col gap-6" data-testid="viz-analytics-overview">
      <ReadingCard items={reading} loading={loading} />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {kpis.map((k) => (
          <KpiCard
            key={k.key}
            testId={`viz-analytics-kpi-${k.key}`}
            label={k.label}
            value={k.percent ? `${formatNumber(valueOf(k.m), 1)}%` : formatNumber(valueOf(k.m))}
            delta={deltaOf(k.m)}
            hint={vsPrevious}
            icon={k.icon}
            trend={k.spark && data ? sparkOf(k.m, period, granularity) : undefined}
            loading={loading}
          />
        ))}
      </div>

      <SectionCard
        title={t(`VIZ_ANALYTICS.TREND.${trendMetric.toUpperCase()}_TITLE`)}
        description={t(`VIZ_ANALYTICS.TREND.DESCRIPTION_${granularity.toUpperCase()}`)}
        action={
          <DashboardExportMenu
            data={data?.dashboard ?? null}
            dateRange={{ startDate: period.startDate, endDate: period.endDate }}
            kpis={kpis.map((k) => ({ label: k.label, value: formatNumber(valueOf(k.m)), change: k.m?.change ?? 0 }))}
          />
        }
        testId="viz-analytics-trend"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <PillTabs<TrendMetric>
            value={trendMetric}
            onChange={setTrendMetric}
            ariaLabel={t('VIZ_ANALYTICS.TREND.METRIC')}
            options={(Object.keys(TREND_METRICS) as TrendMetric[]).map((m) => ({ value: m, label: t(`VIZ_ANALYTICS.TREND.METRIC_${m.toUpperCase()}`) }))}
          />
          <PillTabs<Granularity>
            value={granularity}
            onChange={setGranularity}
            ariaLabel={t('VIZ_ANALYTICS.TREND.GRANULARITY')}
            options={(['day', 'week', 'month'] as Granularity[]).map((g) => ({ value: g, label: t(`VIZ_ANALYTICS.TREND.GRAIN_${g.toUpperCase()}`) }))}
          />
        </div>
        <TrendChart
          data={data ? trendOf(series[trendMetric], period, granularity, locale) : []}
          valueLabel={t('VIZ_ANALYTICS.TREND.THIS_PERIOD')}
          previousLabel={t('VIZ_ANALYTICS.TREND.PREVIOUS_PERIOD')}
          // The period runs to today: the last bucket is still filling up.
          partialLast={period.endDate >= utcDay(Date.now())}
          partialLabel={t('VIZ_ANALYTICS.TREND.SO_FAR')}
          height={260}
        />
      </SectionCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <VizFunnel
          loading={loading}
          className="min-w-0"
          counts={{
            visitors: valueOf(data?.visitors),
            tried: valueOf(data?.trialVisitors),
            saved: valueOf(data?.resultVisitors),
            leads: valueOf(data?.leads),
          }}
        />
        <SectionCard title={t('VIZ_ANALYTICS.DEVICES.TITLE')} description={t('VIZ_ANALYTICS.DEVICES.DESCRIPTION')} className="min-w-0" testId="viz-devices">
          <div className="flex flex-col gap-6">
            {devices.length ? (
              <DistributionBar
                columns={3}
                segments={devices.map((d) => ({ key: d.key, label: t(`VIZ_ANALYTICS.DEVICE.${deviceKey(d.key)}`), value: d.value }))}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{t('VIZ_ANALYTICS.COMMON.NO_DATA')}</p>
            )}
            <div>
              <p className="mb-3 text-xs text-muted-foreground">{t('VIZ_ANALYTICS.SURFACES.TITLE')}</p>
              {surfaces.length ? (
                <DistributionBar
                  columns={4}
                  segments={surfaces.map((s) => ({
                    key: s.key,
                    label: t(`VIZ_ANALYTICS.SURFACE.${(SURFACES as readonly string[]).includes(s.key) ? s.key.toUpperCase() : 'OTHER'}`),
                    value: s.value,
                  }))}
                />
              ) : (
                <p className="text-sm text-muted-foreground">{t('VIZ_ANALYTICS.COMMON.NO_DATA')}</p>
              )}
            </div>
          </div>
        </SectionCard>
      </div>

      <SectionCard title={t('VIZ_ANALYTICS.GEO.TITLE')} description={t('VIZ_ANALYTICS.GEO.DESCRIPTION_LONG')} testId="viz-geo">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="min-w-0">
            <p className="mb-2 text-xs text-muted-foreground">{t('VIZ_ANALYTICS.GEO.COUNTRIES')}</p>
            <BarList
              emptyText={t('VIZ_ANALYTICS.GEO.EMPTY')}
              items={(geo?.topCountries ?? []).slice(0, 8).map((c) => ({ key: c.country, label: c.country, value: c.uniqueUsers }))}
            />
          </div>
          <div className="min-w-0">
            <p className="mb-2 text-xs text-muted-foreground">{t('VIZ_ANALYTICS.GEO.CITIES')}</p>
            <BarList
              emptyText={t('VIZ_ANALYTICS.GEO.EMPTY')}
              items={(data?.cities?.breakdown ?? []).map((c) => ({ key: c.key, label: c.key, value: c.value }))}
            />
          </div>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title={t('VIZ_ANALYTICS.BEHAVIOUR.TITLE')} description={t('VIZ_ANALYTICS.BEHAVIOUR.DESCRIPTION')} className="min-w-0" testId="viz-behaviour">
          <BarList
            emptyText={t('VIZ_ANALYTICS.COMMON.NO_DATA')}
            items={behaviour.map((b) => ({ key: b.key, label: t(`VIZ_ANALYTICS.BEHAVIOUR.${b.key.toUpperCase()}`), value: b.value }))}
          />
        </SectionCard>
        <SectionCard title={t('VIZ_ANALYTICS.HOURS.TITLE')} description={t('VIZ_ANALYTICS.HOURS.DESCRIPTION')} className="min-w-0" testId="viz-hours">
          {hasHours ? (
            <HourStrip hours={hours} />
          ) : (
            <p className="text-sm text-muted-foreground">{t('VIZ_ANALYTICS.COMMON.NO_DATA')}</p>
          )}
        </SectionCard>
      </div>

      <div id="searches" className="scroll-mt-24">
        <SectionCard
          title={
            <TermLabel label={t('VIZ_ANALYTICS.SEARCHES.WHAT')} tip={t('VIZ_ANALYTICS.SEARCHES.TIP')}>
              {t('VIZ_ANALYTICS.SEARCHES.TITLE')}
            </TermLabel>
          }
          description={t('VIZ_ANALYTICS.SEARCHES.DESCRIPTION')}
          action={
            <Button asChild variant="outline" size="sm">
              <Link to={VIZ_LINKS.newProduct}>
                <Plus />
                {t('VIZ_ANALYTICS.SEARCHES.ADD_PRODUCT')}
              </Link>
            </Button>
          }
          testId="viz-searches"
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="flex gap-8 md:flex-col md:gap-5">
              <MiniStat label={t('VIZ_ANALYTICS.SEARCHES.TOTAL')} value={formatNumber(valueOf(data?.searches))} />
              <MiniStat
                label={t('VIZ_ANALYTICS.SEARCHES.EMPTY_RESULTS')}
                value={formatNumber(valueOf(data?.zeroSearches))}
                sub={
                  valueOf(data?.searches)
                    ? t('VIZ_ANALYTICS.SEARCHES.SHARE', { percent: Math.round(((valueOf(data?.zeroSearches) ?? 0) / (valueOf(data?.searches) || 1)) * 100) })
                    : undefined
                }
              />
            </div>
            <div className="min-w-0 md:col-span-2">
              <p className="mb-2 text-xs text-muted-foreground">{t('VIZ_ANALYTICS.SEARCHES.TERMS')}</p>
              <BarList
                emptyText={t('VIZ_ANALYTICS.SEARCHES.NO_TERMS')}
                items={(data?.zeroTerms ?? []).slice(0, 8).map((term) => ({
                  key: term.term,
                  label: `“${term.term}”`,
                  value: term.count,
                  secondary: t('VIZ_ANALYTICS.SEARCHES.VISITORS', { count: term.visitors }),
                }))}
              />
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

function deviceKey(key: string): string {
  if (/mobile|phone/i.test(key)) return 'MOBILE';
  if (/tablet/i.test(key)) return 'TABLET';
  if (/desktop|web|computer/i.test(key)) return 'DESKTOP';
  return 'OTHER';
}

export default ModernAnalyticsDashboard;
